'use client'

import React, { Component, ReactNode, ErrorInfo } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  errorMessage: string
}

/**
 * Specialized Error Boundary for EllipsisMeasure infinite loop errors
 * Catches and suppresses the specific "Maximum update depth exceeded" errors
 * caused by EllipsisMeasure components
 */
export class EllipsisErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, errorMessage: '' }
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is an EllipsisMeasure-related error
    const errorMessage = error.message || error.toString()

    if (
      errorMessage.includes('Maximum update depth exceeded') ||
      errorMessage.includes('EllipsisMeasure') ||
      errorMessage.includes('componentWillUpdate') ||
      errorMessage.includes('setState inside componentWillUpdate') ||
      errorMessage.includes('Ellipsis.js')
    ) {
      // Suppress the error and prevent rendering
      return { hasError: true, errorMessage }
    }

    // Re-throw non-ellipsis errors
    throw error
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorMessage = error.message || error.toString()

    // Only handle EllipsisMeasure-related errors
    if (
      errorMessage.includes('Maximum update depth exceeded') ||
      errorMessage.includes('EllipsisMeasure') ||
      errorMessage.includes('componentWillUpdate') ||
      errorMessage.includes('setState inside componentWillUpdate') ||
      errorMessage.includes('Ellipsis.js')
    ) {
      // Suppress console errors for these specific cases
      return
    }

    // Re-throw other errors to be handled by parent error boundaries
    throw error
  }

  override render() {
    if (this.state.hasError) {
      // Return fallback or null to suppress problematic components
      return this.props.fallback || null
    }

    return this.props.children
  }
}

export default EllipsisErrorBoundary

